import { precisionTrunc } from "utils/utils";

export const formBuilderDefinedSanitizers = {
  // decimal:[precision]
  decimal: {
    argParser: /^([0-9])$/,
    factory: (precision) => (value, prevValue) => {
      if (typeof value !== "number") return prevValue;
      return precisionTrunc(value, precision)
    },
    transformer: (value, prevValue) => {
      if (typeof value !== "number") return prevValue;
      return value;
    }
  },
  integer: {
    transformer: (value, prevValue) => {
      if (typeof value !== "number") return prevValue;
      return Math.trunc(value);
    },
  },
  /**
   * range:[min,max] (inclusive)
   * note: should be used with decimal/integer
   */
  range: {
    argParser: /^\[([0-9]*),([0-9]*)\]$/,
    factory: (min, max) => (value, prevValue) => {
      const [minNum, maxNum] = [parseInt(min), parseInt(max)];
      if (!isNaN(maxNum) && (value > maxNum)) return prevValue;
      if (!isNaN(minNum) && (value < minNum)) {
        const absValue = Math.abs(value);
        if (absValue <= maxNum && absValue >= minNum) return absValue;
        return prevValue;
      }
      return value;
    }
  },
  alphaNumericOnly: {
    transformer: (value) => {
      return value.replace(/[^a-zA-Z0-9]/g, "");
    }
  },
  numericOnly: {
    transformer: (value) => {
      return value.replace(/[^0-9]/g, "");
    }
  }
}

/**
 * @typedef {(value: number | string, prevValue: number | string) => number | string} transformer
 * 
 * @typedef {Object} Sanitizer
 * @property {transformer} transformer
 * @property {RegExp} argParser - Regular expression for dynamic sanitization
 * @property {(...args: any) => transformer} factory - Factory for dynamic sanitization
 * @property {boolean} [alwaysRun=false] - Don't skip sanitization if value is falsy
 */

/**
 * @typedef {import("react-hook-form").UseFormReturn} formPropsType
 * @typedef {keyof formBuilderDefinedSanitizers} definedSanitizersName
 */

/**
 * @param {Object} props
 * @param {definedSanitizersName[]} props.sanitizerNames
 * 
 * @returns {(value: number | string, prevValue?: number | string) => number | string}
 */
export const getComposedSanitizer = ({ sanitizerNames } = {}) => {
  if (!sanitizerNames?.length) return (value) => value;

  /** @type {Sanitizer[]} */
  const sanitizers = sanitizerNames.map((sanitizerName) => {
    const [mainName, params] = sanitizerName.split(":");

    /** @type {Sanitizer} */
    const sanitizer = formBuilderDefinedSanitizers[mainName];
    const useFactory = sanitizer?.factory && sanitizer?.argParser && params;
    if (!sanitizer) throw new Error(`Sanitizer ${sanitizerName} not found`);

    return {
      ...sanitizer,
      ...useFactory && {
        transformer: sanitizer.factory(...sanitizer.argParser.exec(params).slice(1)),
      }
    }
  });
  return (value, prevValue) => sanitizers.reduce(
    (acc, sanitizer) => {
      // Skip the sanitizer if there is there's no value (Mandatory rule will catch this if the value is required)
      // Use alwaysRun to force the sanitizer to run even if the value is falsy
      // Make exception for number (0)
      if (!acc && acc !== 0 && !sanitizer.alwaysRun) return acc;

      return sanitizer.transformer(acc, prevValue);
    },
    value
  );
}